import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  Contact,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  formatCrmDateTime,
  getCustomerInitials,
  getLeadStatusBadgeClass,
  getLeadStatusMeta,
  LEAD_TONE_DOT,
} from "@/utils/crmHelpers";

const CrmCustomersPage = ({
  isDark,
  textClass,
  labelClass,
  inputClass,
  leadStatuses,
  companies,
  isSuperAdmin,
  crmSearchInput,
  setCrmSearchInput,
  crmSearchTerm,
  applyCrmSearch,
  clearCrmSearch,
  crmLeadFilter,
  setCrmLeadFilter,
  crmCompanyFilter,
  setCrmCompanyFilter,
  crmPage,
  setCrmPage,
  crmTotal,
  crmLoading,
  crmCustomers,
  refreshCrmCustomers,
  onCustomerClick,
}) => {
  const totalPages = Math.ceil(crmTotal / 20) || 1;
  const shellClass = isDark
    ? "rounded-2xl border border-slate-800/80 bg-slate-950/50"
    : "rounded-2xl border border-gray-200 bg-white shadow-sm";

  return (
    <div className="space-y-4">
      <div className={`${shellClass} p-5 sm:p-6`}>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isDark ? "bg-violet-500/20" : "bg-violet-100"}`}>
                <Contact className={`w-4 h-4 ${isDark ? "text-violet-400" : "text-violet-600"}`} />
              </div>
              <h2 className={`text-lg font-semibold ${textClass}`}>Müşteriler</h2>
            </div>
            <p className={`text-sm ${labelClass}`}>Basit CRM — müşteri kartları, lead durumu ve görüşme geçmişi</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className={isDark ? "bg-slate-800 text-slate-300" : ""}>
              {crmTotal.toLocaleString("tr-TR")} kayıt
            </Badge>
            <Button variant="ghost" size="sm" onClick={refreshCrmCustomers} disabled={crmLoading} className={labelClass}>
              <RefreshCw className={`w-4 h-4 ${crmLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-3 mt-5">
          <div className="relative flex-1 max-w-lg">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
            <Input
              placeholder="Telefon veya ad soyad ara..."
              value={crmSearchInput}
              onChange={(e) => setCrmSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyCrmSearch(); }}
              className={`pl-10 ${inputClass}`}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={applyCrmSearch} className={isDark ? "border-slate-700" : ""}>Ara</Button>
            {crmSearchTerm && (
              <Button variant="ghost" size="sm" onClick={clearCrmSearch} className={labelClass}>Temizle</Button>
            )}
            <Select value={crmLeadFilter} onValueChange={(v) => { setCrmLeadFilter(v); setCrmPage(1); }}>
              <SelectTrigger className={`w-40 ${inputClass}`}><SelectValue placeholder="Lead durumu" /></SelectTrigger>
              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                <SelectItem value="all">Tüm durumlar</SelectItem>
                {leadStatuses.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isSuperAdmin && (
              <Select value={crmCompanyFilter} onValueChange={(v) => { setCrmCompanyFilter(v); setCrmPage(1); }}>
                <SelectTrigger className={`w-44 ${inputClass}`}><SelectValue placeholder="Firma" /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tüm firmalar</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Lead durumu hızlı filtre */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 mt-4 scrollbar-thin">
          <button
            type="button"
            onClick={() => { setCrmLeadFilter("all"); setCrmPage(1); }}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              crmLeadFilter === "all"
                ? (isDark ? "bg-violet-600 text-white border-violet-500" : "bg-violet-600 text-white border-violet-600")
                : (isDark ? "border-slate-700 text-slate-400 hover:border-slate-600" : "border-gray-200 text-gray-600 hover:border-gray-300")
            }`}
          >
            Tümü
          </button>
          {leadStatuses.map((s) => {
            const active = crmLeadFilter === s.value;
            const tone = s.tone || "violet";
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => { setCrmLeadFilter(s.value); setCrmPage(1); }}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? getLeadStatusBadgeClass(s.value, isDark, leadStatuses)
                    : (isDark ? "border-slate-700 text-slate-400 hover:border-slate-600" : "border-gray-200 text-gray-600 hover:border-gray-300")
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${LEAD_TONE_DOT[tone] || LEAD_TONE_DOT.violet}`} />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {crmLoading ? (
        <div className={`${shellClass} flex justify-center py-20`}>
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      ) : crmCustomers.length === 0 ? (
        <div className={`${shellClass} py-16 text-center`}>
          <Contact className={`w-12 h-12 mx-auto mb-3 opacity-25 ${isDark ? "text-violet-400" : "text-violet-600"}`} />
          <p className={labelClass}>
            {crmSearchTerm || crmLeadFilter !== "all" ? "Arama sonucu bulunamadı" : "Henüz CRM kaydı yok"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {crmCustomers.map((c) => {
            const phone = c.phone || c.phone_normalized;
            const name = c.ad_soyad?.trim();
            const leadMeta = getLeadStatusMeta(c.lead_durumu, leadStatuses);
            const initials = getCustomerInitials(name, phone);
            return (
              <button
                key={`${c.phone_normalized}-${c.id || "new"}`}
                type="button"
                onClick={() => onCustomerClick(phone)}
                className={`${shellClass} text-left p-4 transition-all hover:scale-[1.01] hover:shadow-md ${
                  isDark ? "hover:border-violet-500/40 hover:bg-slate-900/80" : "hover:border-violet-200 hover:shadow-violet-100/50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-sm font-bold shrink-0 ${
                    isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-100 text-violet-700"
                  }`}>
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`font-semibold truncate ${textClass}`}>
                      {name || <span className={labelClass}>İsimsiz müşteri</span>}
                    </p>
                    <p className={`font-mono text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{phone}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <Badge className={`border text-[10px] ${getLeadStatusBadgeClass(c.lead_durumu, isDark, leadStatuses)}`}>
                        {c.lead_durumu_label || leadMeta?.label}
                      </Badge>
                      {c.lead_kaynagi_label && (
                        <span className={`text-[10px] ${labelClass}`}>{c.lead_kaynagi_label}</span>
                      )}
                    </div>
                    {isSuperAdmin && c.company_name && (
                      <p className={`text-[10px] mt-1.5 ${labelClass}`}>{c.company_name}</p>
                    )}
                    <p className={`text-[10px] mt-2 ${labelClass}`}>
                      {c.updated_at ? formatCrmDateTime(c.updated_at) : "—"}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className={`${shellClass} flex items-center justify-between px-4 py-3`}>
          <div className={`text-sm ${labelClass}`}>Sayfa {crmPage} / {totalPages}</div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setCrmPage(1)} disabled={crmPage === 1}>İlk</Button>
            <Button variant="ghost" size="sm" onClick={() => setCrmPage((p) => Math.max(1, p - 1))} disabled={crmPage === 1}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className={`px-3 py-1 rounded-lg text-sm ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>{crmPage}</span>
            <Button variant="ghost" size="sm" onClick={() => setCrmPage((p) => Math.min(totalPages, p + 1))} disabled={crmPage === totalPages}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCrmPage(totalPages)} disabled={crmPage === totalPages}>Son</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmCustomersPage;
